import React, { useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../lib/supabaseClient';

export interface DownloadableDocument { id: string; file_path: string; filename: string; user_id?: string | null; type?: string | null }
export const FILE_REQUEST_TIMEOUT_MS = 12000;
const SYSTEM_DOCUMENT_TYPES = new Set(['submission_form_pdf', 'submission_form_markdown', 'decision_letter', 'amendment_decision_letter', 'pi_attest_doc_signed', 'pi_attest_doc_unsigned', 'stamped_icf']);

async function withTimeout<T>(request: PromiseLike<T>): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([Promise.resolve(request), new Promise<T>((_, reject) => {
      timer = setTimeout(() => reject(new Error('The file request timed out. Please retry.')), FILE_REQUEST_TIMEOUT_MS);
    })]);
  } finally { clearTimeout(timer); }
}

export function DocumentDownloadLink({ document, client = getSupabaseClient(), onAvailabilityChange }: {
  document: DownloadableDocument; client?: ReturnType<typeof getSupabaseClient>;
  onAvailabilityChange?: (state: 'loading' | 'ready' | 'error') => void;
}) {
  const [state, setState] = useState<'loading' | 'ready' | 'downloading' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [attempt, setAttempt] = useState(0);
  const availabilityCallback = useRef(onAvailabilityChange);
  availabilityCallback.current = onAvailabilityChange;
  useEffect(() => { availabilityCallback.current?.(state === 'downloading' ? 'ready' : state); }, [state]);
  const bucket = SYSTEM_DOCUMENT_TYPES.has(document.type || '') ? 'tempus-created-docs' : 'user-uploaded-docs';
  useEffect(() => {
    let mounted = true;
    setState('loading');
    const check = async () => {
      try {
        const { data, error } = await withTimeout(client.storage.from(bucket).createSignedUrl(document.file_path, 60));
        if (error || !data?.signedUrl) throw new Error('File unavailable. Upload it again or contact support.');
        if (mounted) setState('ready');
      } catch (error) {
        if (mounted) { setMessage(error instanceof Error ? error.message : 'Unable to load this file.'); setState('error'); }
      }
    };
    void check();
    return () => { mounted = false; };
  }, [client, bucket, document.id, document.file_path, attempt]);

  const download = async () => {
    setState('downloading');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FILE_REQUEST_TIMEOUT_MS);
    try {
      // Generate a fresh link on every click so an open tab never holds an expired link.
      const { data, error } = await withTimeout(client.storage.from(bucket).createSignedUrl(document.file_path, 60));
      if (error || !data?.signedUrl) throw new Error('File unavailable. Upload it again or contact support.');
      const response = await fetch(data.signedUrl, { signal: controller.signal });
      if (!response.ok) throw new Error('The file could not be downloaded. Please retry or contact support.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url; anchor.download = document.filename;
      window.document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setState('ready');
    } catch (error) {
      setMessage(error instanceof Error && error.name !== 'AbortError' ? error.message : 'The file request timed out. Please retry.');
      setState('error');
    } finally { clearTimeout(timer); }
  };
  return <span className="inline-flex flex-wrap items-center gap-2 text-sm">
    {state === 'ready' ? <button type="button" onClick={download} className="text-blue-700 underline break-all text-left">{document.filename}</button>
      : <span className="break-all">{document.filename}</span>}
    {(state === 'loading' || state === 'downloading') && <span role="status" className="text-gray-500">{state === 'loading' ? 'Checking file…' : 'Downloading…'}</span>}
    {state === 'error' && <><span role="alert" className="text-red-700">{message}</span><button type="button" onClick={() => setAttempt(n => n + 1)} className="underline text-blue-700">Retry file</button></>}
  </span>;
}
